import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useAppSelector } from "hooks/redux";
import { Canvas } from "../../canvas/Canvas";
import { EditorFacilityProvider } from "../../canvas/EditorFacilityContext";
import { useEditorActions, useEditorStore } from "../../domain/redux/hooks";
import { emailBuilderFacilityProfile } from "../../operations/emailBuilderFacilityProfile";
import { selectDocument } from "../../domain/selectors";
import { shouldSuppressEmailBuilderCanvasClearForTextSelection } from "../../editing/emailBuilderCanvasBlockInteractions";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";
import { useEmailBuilderMobileCanvasScale } from "./useEmailBuilderMobileCanvasScale";
import { EMAIL_BUILDER_CONTENT_WIDTH_PX } from "../emailBuilderVisualConstants";
import { prefetchEmailBuilderLibraryMediaSources } from "../../operations/emailBuilderMediaAdapter";
import { releaseEmailBuilderCanvasKeyboardFocus } from "./settings/emailBuilderSettingsFocus";

function isInlineTextSelectionGestureActive(
  canvasRoot: HTMLElement | null,
): boolean {
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0 || canvasRoot === null) {
    return false;
  }

  const range = selection.getRangeAt(0);
  const anchorNode = range.commonAncestorContainer;
  const anchorElement =
    anchorNode instanceof Element ? anchorNode : anchorNode.parentElement;
  const contentEditable =
    anchorElement?.closest('[contenteditable="true"]') ?? null;

  return shouldSuppressEmailBuilderCanvasClearForTextSelection({
    hasNonCollapsedSelection: !selection.isCollapsed,
    selectionInsideCanvasContentEditable:
      contentEditable !== null && canvasRoot.contains(contentEditable),
  });
}

export function EmailBuilderCanvas() {
  const { isMobileLayout } = useEmailBuilderViewport();
  const { setSelection } = useEditorActions();
  const adminFacility = useAppSelector(store => store.facilityStore.facility);
  const facility = useMemo(
    () => emailBuilderFacilityProfile(adminFacility),
    [adminFacility],
  );
  const pageBackgroundColor = useEditorStore(
    state => selectDocument(state).settings.pageBackgroundColor,
  );
  const canvasSurfaceRef = useRef<HTMLDivElement>(null);
  const canvasRootRef = useRef<HTMLDivElement>(null);
  const canvasFrameRef = useRef<HTMLDivElement>(null);
  const { scale: mobileCanvasScale, scaledHeightPx: mobileCanvasScaledHeightPx } =
    useEmailBuilderMobileCanvasScale(canvasRootRef, canvasFrameRef, isMobileLayout);
  const [mediaSourcesVersion, setMediaSourcesVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void prefetchEmailBuilderLibraryMediaSources().then(() => {
      if (!cancelled) {
        setMediaSourcesVersion(version => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const clearCanvasSelection = useCallback(
    (canvasRoot: HTMLElement) => {
      if (isInlineTextSelectionGestureActive(canvasRoot)) {
        return;
      }
      setSelection({ kind: "email" });
      releaseEmailBuilderCanvasKeyboardFocus();
    },
    [setSelection],
  );

  useEffect(() => {
    const surface = isMobileLayout ? canvasRootRef.current : canvasSurfaceRef.current;
    if (surface === null) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || event.target !== surface) {
        return;
      }
      clearCanvasSelection(surface);
    };

    surface.addEventListener("pointerdown", onPointerDown);
    return () => surface.removeEventListener("pointerdown", onPointerDown);
  }, [clearCanvasSelection, isMobileLayout]);

  if (!isMobileLayout) {
    return (
      <EditorFacilityProvider
        facility={facility}
        mediaSourcesVersion={mediaSourcesVersion}>
        <div
          ref={canvasSurfaceRef}
          className="email-builder-canvas"
          style={{ backgroundColor: pageBackgroundColor }}>
          <Canvas facility={facility} />
        </div>
      </EditorFacilityProvider>
    );
  }

  return (
    <EditorFacilityProvider
      facility={facility}
      mediaSourcesVersion={mediaSourcesVersion}>
      <div
        ref={canvasRootRef}
        className={classNames("email-builder-canvas", "email-builder-canvas--mobile-layout")}
        style={{
          backgroundColor: pageBackgroundColor,
          "--email-builder-mobile-canvas-scale": String(mobileCanvasScale),
        } as React.CSSProperties}>
        <div
          className="email-builder-canvas-frame-scaler"
          style={
            mobileCanvasScaledHeightPx !== null
              ? {
                  width: EMAIL_BUILDER_CONTENT_WIDTH_PX * mobileCanvasScale,
                  height: mobileCanvasScaledHeightPx,
                }
              : {
                  width: EMAIL_BUILDER_CONTENT_WIDTH_PX * mobileCanvasScale,
                }
          }>
          <div
            ref={canvasFrameRef}
            className="email-builder-canvas-frame email-builder-canvas-frame--mobile-scaled"
            style={{ width: `${EMAIL_BUILDER_CONTENT_WIDTH_PX}px` }}>
            <Canvas facility={facility} />
          </div>
        </div>
      </div>
    </EditorFacilityProvider>
  );
}
